import { DateTime, Interval } from "luxon";

/**
 * Period math — the single place period boundaries are computed.
 * All persistent bucketing goes through the generated CalendarPeriod table;
 * this module is what generates it (and what tests verify).
 *
 * Week rule (from the source sheets, NOT ISO): week 1 of year Y is the week
 * (starting on the org's week start day) that CONTAINS Jan 1 of Y. Weeks run
 * consecutively until the week that contains Jan 1 of Y+1 (exclusive) — so a
 * year has 52 or 53 weeks, and late-December days can belong to W01 of the
 * next year.
 *
 * Quarters/years are fiscal-aware: Q1 = the 3 months starting at
 * fiscalYearStartMonth; the fiscal year is labeled by its starting calendar year.
 */

export type PeriodType = "day" | "week" | "month" | "quarter" | "year";

export interface OrgCalendarConfig {
  timezone: string; // IANA
  weekStartDay: number; // 1 = Monday ... 7 = Sunday (Luxon convention)
  fiscalYearStartMonth: number; // 1-12
}

export interface Period {
  periodType: PeriodType;
  periodKey: string;
  /** inclusive start, as UTC instant of org-local midnight */
  startsAt: Date;
  /** exclusive end */
  endsAt: Date;
}

const DAY_KEY = "yyyy-MM-dd";

function local(config: OrgCalendarConfig, d: Date): DateTime {
  return DateTime.fromJSDate(d, { zone: config.timezone });
}

/** Start of the week containing dt, per org week start day. */
function startOfWeek(dt: DateTime, weekStartDay: number): DateTime {
  const d = dt.startOf("day");
  const diff = (d.weekday - weekStartDay + 7) % 7;
  return d.minus({ days: diff });
}

/** The week-year that the week starting at weekStart belongs to, plus week number. */
function weekYearAndNumber(
  weekStart: DateTime,
  weekStartDay: number,
): { year: number; week: number } {
  const weekEnd = weekStart.plus({ days: 7 });
  // If Jan 1 of some year falls inside [weekStart, weekEnd), the week belongs to that year as W01+
  // Otherwise it belongs to the year of the most recent Jan 1 at or before weekStart.
  for (const y of [weekStart.year + 1, weekStart.year]) {
    const jan1 = DateTime.fromObject({ year: y, month: 1, day: 1 }, { zone: weekStart.zone });
    const w1Start = startOfWeek(jan1, weekStartDay);
    if (weekStart >= w1Start) {
      const week = Math.floor(weekStart.diff(w1Start, "days").days / 7) + 1;
      return { year: y, week };
    }
  }
  throw new Error("unreachable: week-year resolution failed");
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Fiscal quarter for a local date. Returns fiscal year label + quarter 1-4. */
function fiscalQuarter(dt: DateTime, fiscalStart: number): { fy: number; q: number } {
  // months since fiscal start (0-11)
  const offset = (dt.month - fiscalStart + 12) % 12;
  const q = Math.floor(offset / 3) + 1;
  const fy = dt.month >= fiscalStart ? dt.year : dt.year - 1;
  return { fy, q };
}

/** periodKey for the period of the given type containing the given instant. */
export function periodKeyFor(config: OrgCalendarConfig, type: PeriodType, at: Date): string {
  const dt = local(config, at);
  switch (type) {
    case "day":
      return dt.toFormat(DAY_KEY);
    case "week": {
      const ws = startOfWeek(dt, config.weekStartDay);
      const { year, week } = weekYearAndNumber(ws, config.weekStartDay);
      return `${year}-W${pad2(week)}`;
    }
    case "month":
      return dt.toFormat("yyyy-MM");
    case "quarter": {
      const { fy, q } = fiscalQuarter(dt, config.fiscalYearStartMonth);
      return `${fy}-Q${q}`;
    }
    case "year": {
      const fy = dt.month >= config.fiscalYearStartMonth ? dt.year : dt.year - 1;
      return String(fy);
    }
  }
}

/** Materialize a full Period (bounds) from a periodKey. */
export function periodFromKey(config: OrgCalendarConfig, type: PeriodType, key: string): Period {
  const zone = config.timezone;
  let start: DateTime;
  let end: DateTime;
  switch (type) {
    case "day": {
      start = DateTime.fromFormat(key, DAY_KEY, { zone });
      end = start.plus({ days: 1 });
      break;
    }
    case "week": {
      const m = key.match(/^(\d{4})-W(\d{2})$/);
      if (!m) throw new Error(`bad week key: ${key}`);
      const year = Number(m[1]);
      const week = Number(m[2]);
      const jan1 = DateTime.fromObject({ year, month: 1, day: 1 }, { zone });
      const w1 = startOfWeek(jan1, config.weekStartDay);
      start = w1.plus({ weeks: week - 1 });
      end = start.plus({ weeks: 1 });
      break;
    }
    case "month": {
      start = DateTime.fromFormat(key, "yyyy-MM", { zone });
      end = start.plus({ months: 1 });
      break;
    }
    case "quarter": {
      const m = key.match(/^(\d{4})-Q([1-4])$/);
      if (!m) throw new Error(`bad quarter key: ${key}`);
      const fy = Number(m[1]);
      const q = Number(m[2]);
      start = DateTime.fromObject(
        { year: fy, month: config.fiscalYearStartMonth, day: 1 },
        { zone },
      ).plus({ months: (q - 1) * 3 });
      end = start.plus({ months: 3 });
      break;
    }
    case "year": {
      const fy = Number(key);
      start = DateTime.fromObject(
        { year: fy, month: config.fiscalYearStartMonth, day: 1 },
        { zone },
      );
      end = start.plus({ years: 1 });
      break;
    }
  }
  if (!start!.isValid) throw new Error(`invalid period key ${type}:${key}`);
  return {
    periodType: type,
    periodKey: key,
    startsAt: start!.toUTC().toJSDate(),
    endsAt: end!.toUTC().toJSDate(),
  };
}

/** All periods of a type that intersect [from, to) in org-local time. */
export function periodsInRange(
  config: OrgCalendarConfig,
  type: PeriodType,
  from: Date,
  to: Date,
): Period[] {
  const out: Period[] = [];
  let key = periodKeyFor(config, type, from);
  let p = periodFromKey(config, type, key);
  while (p.startsAt < to) {
    if (p.endsAt > from) out.push(p);
    key = periodKeyFor(config, type, p.endsAt);
    p = periodFromKey(config, type, key);
  }
  return out;
}

/** Child periods (sub-periods) of a parent period, for rollup math.
 * Chain: week→day; month→day; quarter→month; year→month.
 * (Weeks straddle months, so months never decompose into weeks.) */
export function childPeriodType(type: PeriodType): PeriodType | null {
  switch (type) {
    case "day":
      return null;
    case "week":
      return "day";
    case "month":
      return "day";
    case "quarter":
      return "month";
    case "year":
      return "month";
  }
}

export function childPeriods(config: OrgCalendarConfig, p: Period): Period[] {
  const childType = childPeriodType(p.periodType);
  if (!childType) return [];
  // Weeks can straddle year boundaries; children are all child-periods whose
  // start falls inside the parent.
  return periodsInRange(config, childType, p.startsAt, p.endsAt).filter(
    (c) => c.startsAt >= p.startsAt && c.startsAt < p.endsAt,
  );
}

/** Number of weeks in a week-year (52 or 53). */
export function weeksInYear(config: OrgCalendarConfig, year: number): number {
  const zone = config.timezone;
  const jan1 = DateTime.fromObject({ year, month: 1, day: 1 }, { zone });
  const jan1Next = DateTime.fromObject({ year: year + 1, month: 1, day: 1 }, { zone });
  const w1 = startOfWeek(jan1, config.weekStartDay);
  const w1Next = startOfWeek(jan1Next, config.weekStartDay);
  return Math.round(w1Next.diff(w1, "weeks").weeks);
}

/** Generate every period of every type covering a calendar year (for CalendarPeriod). */
export function generateYearPeriods(config: OrgCalendarConfig, year: number): Period[] {
  const zone = config.timezone;
  const from = DateTime.fromObject({ year, month: 1, day: 1 }, { zone }).toUTC().toJSDate();
  const to = DateTime.fromObject({ year: year + 1, month: 1, day: 1 }, { zone })
    .toUTC()
    .toJSDate();
  const all: Period[] = [];
  for (const t of ["day", "week", "month", "quarter", "year"] as const) {
    all.push(...periodsInRange(config, t, from, to));
  }
  // de-dup (quarter/year/week periods overlap adjacent calendar years)
  const seen = new Set<string>();
  return all.filter((p) => {
    const k = `${p.periodType}:${p.periodKey}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Days (as 'yyyy-MM-dd' local keys) inside a period. */
export function dayKeysInPeriod(config: OrgCalendarConfig, p: Period): string[] {
  const days: string[] = [];
  let d = local(config, p.startsAt).startOf("day");
  const end = local(config, p.endsAt);
  while (d < end) {
    days.push(d.toFormat(DAY_KEY));
    d = d.plus({ days: 1 });
  }
  return days;
}

/** The parent period key of a period one level up the chain (for staleness propagation). */
export function parentPeriodTypes(type: PeriodType): PeriodType[] {
  switch (type) {
    case "day":
      return ["week", "month"];
    case "week":
      return ["year"];
    case "month":
      return ["quarter", "year"];
    case "quarter":
      return ["year"];
    case "year":
      return [];
  }
}

/** Same period, previous year — for YoY comparison. Returns null when the shape
 * doesn't exist in the prior year (e.g., W53). */
export function priorYearKey(
  config: OrgCalendarConfig,
  type: PeriodType,
  key: string,
): string | null {
  switch (type) {
    case "day": {
      const d = DateTime.fromFormat(key, DAY_KEY, { zone: config.timezone });
      const prior = d.minus({ years: 1 });
      return prior.isValid ? prior.toFormat(DAY_KEY) : null;
    }
    case "week": {
      const m = key.match(/^(\d{4})-W(\d{2})$/);
      if (!m) return null;
      const year = Number(m[1]) - 1;
      const week = Number(m[2]);
      if (week > weeksInYear(config, year)) return null;
      return `${year}-W${pad2(week)}`;
    }
    case "month": {
      const d = DateTime.fromFormat(key, "yyyy-MM", { zone: config.timezone });
      return d.isValid ? d.minus({ years: 1 }).toFormat("yyyy-MM") : null;
    }
    case "quarter": {
      const m = key.match(/^(\d{4})-Q([1-4])$/);
      return m ? `${Number(m[1]) - 1}-Q${m[2]}` : null;
    }
    case "year":
      return String(Number(key) - 1);
  }
}

export { Interval, startOfWeek };
